import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { useCopyToClipboard } from 'src/hooks/useCopyToClipboard'

interface Props {
  subscriptionId: string
}

/**
 * The subscription ID in the list (ENG-1221), so users without `webhook_subscriptions_manage` —
 * who cannot open the edit modal — can still read and copy it. Full value on hover.
 */
const WebhookSubscriptionIdCellComponent = ({ subscriptionId }: Props) => {
  const copyToClipboard = useCopyToClipboard()

  const handleCopy = useCallback(
    (event: React.MouseEvent) => {
      // Never let the click reach the row or its actions.
      event.stopPropagation()
      copyToClipboard(subscriptionId, 'Subscription ID copied')
    },
    [copyToClipboard, subscriptionId],
  )

  return (
    <div css={tw`flex min-w-0 flex-row items-center gap-1`}>
      <Tooltip title={subscriptionId}>
        <Typography
          variant="body2"
          css={tw`min-w-0 truncate font-mono text-xs text-shades-500`}
          data-testid={`webhook-subscription-id-${subscriptionId}`}
        >
          {subscriptionId}
        </Typography>
      </Tooltip>
      <Tooltip title="Copy">
        <span css={tw`shrink-0`}>
          <Button
            size="tiny"
            color="grey"
            iconOnly
            leftIcon={
              <GSIcon iconName="gs_square_behind_square_1" size={16} css={tw`text-shades-1000`} />
            }
            aria-label="Copy subscription ID"
            onClick={handleCopy}
            data-testid={`webhook-subscription-copy-id-${subscriptionId}`}
          />
        </span>
      </Tooltip>
    </div>
  )
}

export const WebhookSubscriptionIdCell = memo(WebhookSubscriptionIdCellComponent)
